import { useState, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, PenTool, Clock, TrendingUp, Users, Target, Database, Lightbulb, FileText, Settings, ArrowLeft, Zap, Cog, Activity, Webhook, Calendar, CalendarDays, Globe, ShoppingCart, Shield, Package, Instagram, Cpu, MessageSquare, Smartphone, Key, Navigation, Bot, Plug, TvMinimalPlay, Send, Mail, BarChart3, Kanban, CheckCircle, Rss, Bell, Copy, Layout, LayoutDashboard, LayoutTemplate, Monitor, Brain, Contact, BadgeDollarSign, GraduationCap, Tag, CreditCard, Workflow, UserSquare, UserPlus, Sparkles, Mic, Crosshair, FolderOpen, Album, List, Link as LinkIcon, Search, FileSpreadsheet, Share2, FlaskConical } from "lucide-react";
import { useCustomBrandFeatures } from "@/hooks/useCustomBrandFeatures";
import SuperAdminNavigation from "@/components/SuperAdminNavigation";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSettings } from "@/contexts/SettingsContext";
import { useSidebar } from "@/components/ui/sidebar";
import { useMenuTutorialVideos } from "@/hooks/useMenuTutorialVideos";
import { SettingsMenuItemWithTutorial } from "@/components/navigation/SettingsMenuItemWithTutorial";
import { useTypedTranslation } from "@/lib/i18n/hooks";

export function SettingsSidebar() {
  const { setIsSettingsSidebarOpen } = useSettings();
  const { setOpenMobile } = useSidebar();
  const location = useLocation();
  const { hasFeature } = useCustomBrandFeatures();
  const { isSuperAdmin, isLoading: superAdminLoading } = useSuperAdmin();
  const { settingsVideos } = useMenuTutorialVideos();
  const { tNav } = useTypedTranslation();
  
  // State for collapsible sections - all collapsed by default
  const [openSections, setOpenSections] = useState({
    aiModelTraining: false,
    brandManagement: false,
    logsAudits: false,
    contentMarketing: false,
    newsRoom: false,
    newsfeed: false,
    coldOutreach: false,
    emailServices: false,
    collaborations: false,
    salesOperations: false,
    performanceMarketing: false,
    socialMedia: false,
    seo: false,
    saio: false,
    syntheticPersonas: false,
    focusGroups: false,
    digitalOperations: false,
    sopsTemplates: false,
    growthHacking: false,
    embeddableWidgets: false,
    referralSystem: false,
    cro: false,
    admin: false,
    superAdmin: false,
    linkBuilding: false,
    scale2y3x: false,
    factChecker: false,
    brandNaming: false,
    sellableProducts: false,
    personalCards: false,
    learningCenter: false,
    internalTraining: false,
    surveys: false,
    annotations: false,
    humanResources: false,
    tasks: false,
    businessMetrics: false,
    articles: false,
    cms: false,
    cla: false,
    supportPortal: false,
    podcastHosting: false,
    meetings: false,
    events: false,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const [searchQuery, setSearchQuery] = useState("");
  const normalizedQuery = searchQuery.trim().toLowerCase();

  // Section titles for search (section visible if title or any item title matches)
  const sectionMeta: { key: keyof typeof openSections; title: string }[] = useMemo(() => [
    { key: "aiModelTraining", title: "AI Model Training" },
    { key: "brandManagement", title: "Brand Management" },
    { key: "logsAudits", title: "Logs & Audits" },
    { key: "contentMarketing", title: "Content Marketing" },
    { key: "newsRoom", title: "News Room" },
    { key: "newsfeed", title: "Newsfeed" },
    { key: "coldOutreach", title: "Cold Outreach" },
    { key: "emailServices", title: "Email Services" },
    { key: "collaborations", title: "Collaborations" },
    { key: "salesOperations", title: "Sales" },
    { key: "performanceMarketing", title: "Paid Ads" },
    { key: "socialMedia", title: "Social Media Management" },
    { key: "seo", title: "SEO" },
    { key: "saio", title: "GEO (aka SAIO)" },
    { key: "syntheticPersonas", title: "Synthetic Personas" },
    { key: "focusGroups", title: "Focus Groups" },
    { key: "digitalOperations", title: "Tasks" },
    { key: "sopsTemplates", title: "SOPs" },
    { key: "growthHacking", title: "Growth Hacking" },
    { key: "embeddableWidgets", title: "Embeddable Widgets" },
    { key: "referralSystem", title: tNav('referralSystem') },
    { key: "cro", title: "CRO / UX" },
    { key: "admin", title: "Admin" },
    { key: "superAdmin", title: "Super Admin" },
    { key: "linkBuilding", title: "Link Building" },
    { key: "scale2y3x", title: "Scale at Speed: 2Y3X" },
    { key: "factChecker", title: "Fact Checker" },
    { key: "brandNaming", title: "Brand Naming" },
    { key: "sellableProducts", title: "Sellable Products" },
    { key: "personalCards", title: "Personal Cards" },
    { key: "learningCenter", title: "Learning Center" },
    { key: "internalTraining", title: "Internal Training" },
    { key: "surveys", title: "Surveys" },
    { key: "annotations", title: "Annotations" },
    { key: "humanResources", title: "Human Resources" },
    { key: "tasks", title: "Reports" },
    { key: "businessMetrics", title: "Business Metrics" },
    { key: "cms", title: "CMS" },
    { key: "cla", title: "Competitive Analysis" },
    { key: "supportPortal", title: "Support Portal" },
    { key: "podcastHosting", title: "Podcast Hosting" },
    { key: "meetings", title: "Meetings" },
    { key: "events", title: "Events" },
  ], []);

  // AI Model Training settings items
  const aiModelTrainingSettingsItems = [
    {
      title: "Website Crawler",
      icon: Globe,
      path: "/website-crawler",
    },
    {
      title: "Fine-Tuned Models",
      icon: Cpu,
      path: "/fine-tuning",
    },
    {
      title: "Vector Store Management",
      icon: Database,
      path: "/vector-store",
    },
    {
      title: "Copywriting Instructions",
      icon: PenTool,
      path: "/copywriting-instructions",
    },
    {
      title: "AI Assistants",
      icon: Bot,
      path: "/ai-assistant",
    },
    {
      title: "AI Models",
      icon: Settings,
      path: "/ai-models-config",
    },
    {
      title: "Internal AI Training Status",
      icon: BarChart3,
      path: "/ai-assistant-training",
    },
  ];

  // Brand Management settings items
  const brandManagementSettingsItems = [
    {
      title: "Brand Settings",
      icon: Settings,
      path: "/settings",
    },
    {
      title: "Embed Settings",
      icon: Globe,
      path: "/embed-settings",
    },
    {
      title: "Business Terminology",
      icon: MessageSquare,
      path: "/business-terminology",
    },
    {
      title: "Custom API Tokens",
      icon: Key,
      path: "/custom-api-tokens",
      featureKey: "custom_api_tokens" as const,
    },
    {
      title: "API Tokens Ownership",
      icon: Shield,
      path: "/api-tokens-ownership",
    },
    {
      title: "Partner Clients",
      icon: Users,
      path: "/partners",
    },
  {
    title: "Testimonials Embed Guide",
    icon: Globe,
    path: "/testimonials/embed-guide",
  },
  {
    title: "Testimonials Incoming Guide",
    icon: Webhook,
    path: "/testimonials/webhook-guide",
  },
  {
    title: "Client Logos Embed Guide",
    icon: Globe,
    path: "/client-logos/embed-guide",
  },
  {
    title: "Products Embed Guide",
    icon: Globe,
    path: "/brand-products/embed-guide",
  },
  {
    title: "Products Incoming API",
    icon: Webhook,
    path: "/brand-products/incoming",
  },
    {
      title: "Role Management",
      icon: Cog,
      path: "/role-management",
    },
    {
      title: "Role Permissions",
      icon: Shield,
      path: "/role-permissions",
    },
    {
      title: "System Shortcodes",
      icon: Zap,
      path: "/dynamic-content-library",
    },
    {
      title: "Navigation Settings",
      icon: Navigation,
      path: "/navigation-settings",
    },
  ];

  // Logs & Audits settings items
  const logsAuditsSettingsItems = [
    {
      title: "Activity Log",
      icon: Clock,
      path: "/activity",
    },
    {
      title: "API Calls Audit",
      icon: Activity,
      path: "/api-calls-audit",
    },
    {
      title: "Job Reporting Dashboard",
      icon: Activity,
      path: "/job-reporting",
    },
  ];

  // Digital Operations settings items
  const digitalOperationsSettingsItems = [
    {
      title: "Incoming Tasks Settings",
      icon: Webhook,
      path: "/incoming-tasks-settings",
    },
    {
      title: "Recurring Tasks Rules",
      icon: Calendar,
      path: "/task-scheduling",
    },
    {
      title: "Task Rules",
      icon: Settings,
      path: "/settings/task-rules",
    },
    {
      title: "Manage Tags",
      icon: Tag,
      path: "/settings/manage-tags",
    },
    {
      title: "Notification Settings",
      icon: Bell,
      path: "/tasks-notifications-settings",
    },
  ];

  // SOPs settings items
  const sopsSettingsItems = [
    {
      title: "Template Industries",
      icon: Globe,
      path: "/tmplt-industries",
    },
    {
      title: "Template Usecases",
      icon: Target,
      path: "/tmplt-usecases",
    },
  ];

  // Growth Hacking settings items
  const growthHackingSettingsItems = [
    {
      title: "Sales Channels",
      icon: ShoppingCart,
      path: "/sales-channels",
    },
    {
      title: "Growth Pillars",
      icon: Target,
      path: "/growth-pillars-overview",
    },
    {
      title: "Growth Metrics",
      icon: TrendingUp,
      path: "/growth-metrics-overview",
    },
    {
      title: "Growth Squad",
      icon: Users,
      path: "/growth-squad",
    },
  ];

  // Embeddable Widgets settings items
  const embeddableWidgetsSettingsItems = [
    {
      title: "Public Chat Widget",
      icon: MessageSquare,
      path: "/public-chat-widget-config",
    },
    {
      title: "Business Metrics Feed",
      icon: Activity,
      path: "/business-metrics-feed",
    },
  ];

  // Referral System settings items
  const referralSystemSettingsItems = [
    {
      title: tNav('referralEmailSettings'),
      icon: Mail,
      path: "/referral-system/email-settings",
    },
    {
      title: tNav('referralFeed'),
      icon: Share2,
      path: "/referral-participants-feed",
    },
    {
      title: tNav('referralPrompts'),
      icon: Sparkles,
      path: "/referral/prompts",
    },
  ];

  // SEO settings items
  const seoSettingsItems = [
    {
      title: "Prompts",
      icon: Settings,
      path: "/settings-seo",
    },
    {
      title: "Crawl Settings",
      icon: Globe,
      path: "/seo-crawl-settings",
    },
    {
      title: "Organic/Analytics Integrations",
      icon: Plug,
      path: "/integrations",
    },
  ];

  // Link Building settings items
  const linkBuildingSettingsItems = [
    {
      title: "Email Templates",
      icon: Mail,
      path: "/link-building/email-templates",
    },
  ];

  // Admin settings items
  const adminSettingsItems = [
    {
      title: "Technical Readiness",
      icon: Activity,
      path: "/settings/technical-readiness",
    },
    {
      title: "Menu Tutorial Videos",
      icon: TvMinimalPlay,
      path: "/admin/menu-tutorial-videos",
    },
    {
      title: "Project Progress Config",
      icon: Settings,
      path: "/project-progress-configurator",
    },
    {
      title: "Email Designer",
      icon: Mail,
      path: "/email-designer",
    },
    {
      title: "Experiments",
      icon: FlaskConical,
      path: "/experiments",
    },
    {
      title: "Page Builder",
      icon: Monitor,
      path: "/experiments/document-to-page",
    },
    {
      title: "Data Orchestrator",
      icon: Workflow,
      path: "/experiments/data-orchestrator",
    },
    {
      title: "Image Coordinates",
      icon: Target,
      path: "/experiments/image-coordinates",
    },
    {
      title: "Collaborative Editor",
      icon: Users,
      path: "/experiments/collaborative-editor",
    },
    {
      title: "Dashboard Builder",
      icon: LayoutTemplate,
      path: "/experiments/dashboards",
    },
    {
      title: "Push Notifications",
      icon: Bell,
      path: "/push-notifications",
    },
    {
      title: "Clone Prompts",
      icon: Copy,
      path: "/clone-prompts",
    },
    {
      title: "Clone Menu Tutorial Videos",
      icon: Copy,
      path: "/clone-growthos-courses",
    },
    {
      title: "Services Provisioned",
      icon: Package,
      path: "/services-provisioned",
    },
    {
      title: "Apify Usage Monitor",
      icon: TrendingUp,
      path: "/admin/apify-usage",
    },
    {
      title: "Menu Metadata Manager",
      icon: Globe,
      path: "/admin/route-metadata",
    },
    {
      title: "Configure Mobile Footer",
      icon: Smartphone,
      path: "/mobile-footer-config",
    },
    {
      title: "LLM Source Configuration",
      icon: Brain,
      path: "/admin/llm-config",
    },
    {
      title: "User Activity Tracking",
      icon: Activity,
      path: "/admin/user-activity-tracking",
    },
    {
      title: "User Time Tracking",
      icon: Clock,
      path: "/admin/user-time-tracking",
    },
    {
      title: "Growth Standup Admin",
      icon: Calendar,
      path: "/admin/growth-standup",
    },
    {
      title: "Team Cards",
      icon: UserSquare,
      path: "/admin/team-cards",
    },
    {
      title: "Accounting API Tokens",
      icon: Key,
      path: "/accounting-api-tokens",
    },
    {
      title: "Voice Assistant Tools",
      icon: Mic,
      path: "/admin/voice-assistant-tools",
    },
  ];

  // Support Portal settings items
  const supportPortalSettingsItems = [
    {
      title: "Articles",
      icon: FileText,
      path: "/admin/support-portal/articles",
    },
    {
      title: "Categories",
      icon: FolderOpen,
      path: "/admin/support-portal/categories",
    },
    {
      title: "Videos",
      icon: TvMinimalPlay,
      path: "/admin/support-portal/videos",
    },
  ];

  // Scale at Speed: 2Y3X settings items
  const scale2y3xSettingsItems = [
    {
      title: "Phases",
      icon: Kanban,
      path: "/scale-2y3x/phases",
    },
    {
      title: "Quarterly Sprints",
      icon: CalendarDays,
      path: "/scale-2y3x/sprints",
    },
  ];

  // Fact Checker settings items
  const factCheckerSettingsItems = [
    {
      title: "Prompts (Fact Checker)",
      icon: MessageSquare,
      path: "/fact-checker/prompts",
    },
  ];

  // Brand Naming settings items
  const brandNamingSettingsItems = [
    {
      title: "Naming Prompts",
      icon: Sparkles,
      path: "/naming/prompts",
    },
    {
      title: "Domains API Tokens",
      icon: Key,
      path: "/domains-api-tokens",
    },
    {
      title: "Domain Availability",
      icon: Globe,
      path: "/domain-availability",
    },
  ];

  // Sellable Products settings items
  const sellableProductsSettingsItems = [
    {
      title: "My Store",
      icon: Package,
      path: "/sellable-products/my-store",
    },
    {
      title: "Payment Methods",
      icon: ShoppingCart,
      path: "/sellable-products/payment-methods",
    },
    {
      title: "Default Paths",
      icon: Layout,
      path: "/sellable-products/default-paths",
    },
  ];

  // Personal Cards settings items
  const personalCardsSettingsItems = [
    {
      title: "Personal Cards Settings",
      icon: Contact,
      path: "/personal-cards-settings",
    },
  ];

  // Learning Center settings items
  const learningCenterSettingsItems = [
    {
      title: "General Settings",
      icon: Settings,
      path: "/lms/general-settings",
    },
    {
      title: "Prompts",
      icon: MessageSquare,
      path: "/lms/prompts",
    },
    {
      title: "Permalinks",
      icon: LinkIcon,
      path: "/lms/permalinks",
    },
    {
      title: "Webhook Settings",
      icon: Webhook,
      path: "/lms/webhook-settings",
    },
    {
      title: "Leads Settings",
      icon: Webhook,
      path: "/lms/leads-webhook-settings",
    },
    {
      title: "Notification Settings",
      icon: Bell,
      path: "/lms/notification-settings",
    },
    {
      title: "Stripe Configuration",
      icon: BadgeDollarSign,
      path: "/lms/stripe-config",
    },
    {
      title: "Public Payment API",
      icon: CreditCard,
      path: "/payments/public-api",
    },
    {
      title: "Course Embed Guide",
      icon: Globe,
      path: "/courses/embed-guide",
    },
    {
      title: "Course Embed Preview",
      icon: Monitor,
      path: "/courses/embed",
    },
    {
      title: "Instructors Embed Guide",
      icon: Users,
      path: "/instructors/embed-guide",
    },
    {
      title: "Academies Embed Guide",
      icon: GraduationCap,
      path: "/academies/embed-guide",
    },
  ];

  // Events settings items
  const eventsSettingsItems = [
    {
      title: "Events URL Settings",
      icon: Settings,
      path: "/events/settings",
    },
    {
      title: "Email Settings",
      icon: Mail,
      path: "/events/email-settings",
    },
    {
      title: "Public Path",
      icon: LinkIcon,
      path: "/events/public-path",
    },
    {
      title: "Event List Embed Guide",
      icon: Globe,
      path: "/events/events/embed-guide",
    },
    {
      title: "Single Event Embed Guide",
      icon: Globe,
      path: "/events/event/embed-guide",
    },
    {
      title: "Prompts",
      icon: FileText,
      path: "/events/prompts",
    },
    {
      title: "Leads Webhook Settings",
      icon: Webhook,
      path: "/events/leads-webhook-settings",
    },
  ];

  // CMS settings items (includes articles-related settings)
  const cmsSettingsItems = [
    {
      title: "CMS Settings",
      icon: Settings,
      path: "/settings/cms-settings",
    },
    {
      title: "Articles Embed Guide",
      icon: Globe,
      path: "/articles/embed-guide",
    },
    {
      title: "Authors Embed Guide",
      icon: Users,
      path: "/authors/embed-guide",
    },
    {
      title: "Tags Embed Guide",
      icon: Tag,
      path: "/tags/embed-guide",
    },
    {
      title: "Categories Embed Guide",
      icon: Globe,
      path: "/categories/embed-guide",
    },
    {
      title: "Typography Settings",
      icon: FileText,
      path: "/articles/typography",
    },
    {
      title: "Plugins",
      icon: Plug,
      path: "/cms/plugins",
    },
  ];

  // Internal Training settings items
  const internalTrainingSettingsItems = [
    {
      title: "Prompts",
      icon: MessageSquare,
      path: "/internal-training/prompts",
    },
    {
      title: "Share with people & groups",
      icon: UserPlus,
      path: "/internal-training-shares-settings",
    },
  ];

  // Surveys settings items
  const surveysSettingsItems = [
    {
      title: "Prompts",
      icon: MessageSquare,
      path: "/surveys/prompts",
    },
  ];

  // Annotations settings items
  const annotationsSettingsItems = [
    {
      title: "Prompts",
      icon: MessageSquare,
      path: "/annotations/prompts",
    },
    {
      title: "Settings",
      icon: Settings,
      path: "/annotations/settings",
    },
  ];

  // CLA (Competitive Landscape Analysis) settings items
  const claSettingsItems = [
    {
      title: "Prompts",
      icon: MessageSquare,
      path: "/cla/prompts",
    },
    {
      title: "Settings",
      icon: Settings,
      path: "/cla/settings",
    },
  ];

  // Podcast Hosting settings items
  const podcastHostingSettingsItems = [
    {
      title: "Settings",
      icon: Settings,
      path: "/podcasthost/settings",
    },
    {
      title: "Email Settings",
      icon: Mail,
      path: "/podcasthost/email-settings",
    },
    {
      title: "Prompts",
      icon: MessageSquare,
      path: "/podcasthost/prompts",
    },
    {
      title: "Shows Embed Guide",
      icon: Globe,
      path: "/podcasthost/shows/embed-guide",
    },
    {
      title: "Episodes Embed Guide",
      icon: Globe,
      path: "/podcasthost/episodes/embed-guide",
    },
    {
      title: "Single Episode Embed",
      icon: Globe,
      path: "/podcasthost/episode/embed-guide",
    },
  ];
  const humanResourcesSettingsItems = [
    {
      title: tNav('noteworthyEventTypes') || "Noteworthy Event Types",
      icon: Settings,
      path: "/human-resources/noteworthy-event-types",
    },
    {
      title: tNav('jobPostsEmbedGuide') || "Job Posts Embed Guide",
      icon: Globe,
      path: "/human-resources/job-posts/embed-guide",
    },
    {
      title: tNav('applicantPrompts') || "Applicant Prompts",
      icon: Sparkles,
      path: "/human-resources/applicant-prompts",
    },
  ];

  // Tasks settings items (Reports section)
  const tasksSettingsItems = [
    {
      title: "Share with people & groups",
      icon: UserPlus,
      path: "/report-shares-settings",
    },
    {
      title: "Dashboard Components",
      icon: LayoutDashboard,
      path: "/holdco-views/settings/components",
    },
    {
      title: "HoldCo Access Requests",
      icon: Send,
      path: "/holdco-access-requests",
    },
  ];

  // Business Metrics settings items
  const businessMetricsSettingsItems = [
    {
      title: "Metrics Cockpit",
      icon: Activity,
      path: "/admin/metrics-cockpit",
    },
    {
      title: "Metrics Aggregator",
      icon: TrendingUp,
      path: "/metrics-aggregations",
    },
    {
      title: "Analytics Engine",
      icon: Activity,
      path: "/analytics-engine",
    },
    {
      title: "Business Events Settings",
      icon: Settings,
      path: "/business-metrics-settings",
    },
    {
      title: "Business Event Webhook",
      icon: Webhook,
      path: "/business-event-webhook",
    },
    {
      title: "Incoming Event Log",
      icon: FileText,
      path: "/incoming-event-log",
    },
  ];

  // Meetings settings items
  const meetingsSettingsItems = [
    {
      title: "Meeting Types",
      icon: Calendar,
      path: "/meetings/types",
    },
    {
      title: "Section Types",
      icon: List,
      path: "/meetings/section-types",
    },
    {
      title: "One-to-One Settings",
      icon: Settings,
      path: "/meetings/settings",
    },
  ];

  type SettingsCategory = "Marketing" | "Sales" | "Operations" | "Admin" | "Systems";
  const sectionToItems: Partial<Record<keyof typeof openSections, { title: string; path: string; icon: typeof Globe; featureKey?: string }[]>> = {
    aiModelTraining: aiModelTrainingSettingsItems,
    brandManagement: brandManagementSettingsItems,
    logsAudits: logsAuditsSettingsItems,
    digitalOperations: digitalOperationsSettingsItems,
    sopsTemplates: sopsSettingsItems,
    growthHacking: growthHackingSettingsItems,
    embeddableWidgets: embeddableWidgetsSettingsItems,
    referralSystem: referralSystemSettingsItems,
    seo: seoSettingsItems,
    linkBuilding: linkBuildingSettingsItems,
    admin: adminSettingsItems,
    supportPortal: supportPortalSettingsItems,
    scale2y3x: scale2y3xSettingsItems,
    factChecker: factCheckerSettingsItems,
    brandNaming: brandNamingSettingsItems,
    sellableProducts: sellableProductsSettingsItems,
    personalCards: personalCardsSettingsItems,
    learningCenter: learningCenterSettingsItems,
    events: eventsSettingsItems,
    cms: cmsSettingsItems,
    internalTraining: internalTrainingSettingsItems,
    surveys: surveysSettingsItems,
    annotations: annotationsSettingsItems,
    cla: claSettingsItems,
    podcastHosting: podcastHostingSettingsItems,
    humanResources: humanResourcesSettingsItems,
    tasks: tasksSettingsItems,
    businessMetrics: businessMetricsSettingsItems,
    meetings: meetingsSettingsItems,
  };
  const inlineSectionSearchTitles: Partial<Record<keyof typeof openSections, string[]>> = {
    contentMarketing: [
      "Prompts (Ideation)", "Prompts (Earned Media)", "Prompts (Podcasts)", "Prompts (Press Releases)", "Prompts (Quizzes)", "Prompts (Feature Images)", "Prompts (Mini Games)",
      "API Tokens (Podcasts)", "API Tokens (HubSpot)", "API Tokens (GoHighLevel)", "API Tokens (Slack)", "API Tokens (Google Sheets)", "API Tokens (CMS)", "API Tokens (PPTs/PDFs)", "API Tokens (Earned Networks)",
      "API Tokens (Visual Models)", "API Tokens (HeyGen)", "API Tokens", "Prompts (SM Pillars)", "Prompts (Key Dates)", "Visual Sizes & Dimensions", "Supported AI Creative Models", "Settings (Short-Form)", "Settings (Long-Form)", "Prompts Embed Guide",
    ],
    newsRoom: ["Prompts", "Settings", "API Tokens", "Email Settings"],
    newsfeed: ["AI Prompts", "Newsfeed Embed Guide", "Email Settings"],
    coldOutreach: ["Prompts", "Email Settings"],
    emailServices: ["Email Providers"],
    collaborations: ["Prompts", "Settings"],
    salesOperations: ["Sales Settings", "Prompts (Sales)", "AI Training Feedback"],
    performanceMarketing: ["PM Prompts", "Paid Ads Integrations", "Brief Prompts"],
    socialMedia: ["Instagram Data Collector"],
    saio: ["API Tokens", "Prompts"],
    syntheticPersonas: [],
    focusGroups: ["API Tokens", "Prompts"],
    cro: ["Prompts (CRO)", "API Tokens"],
  };
  const ORDERED_SECTIONS: (keyof typeof openSections)[] = [
    "cms", "syntheticPersonas", "focusGroups", "collaborations", "contentMarketing", "newsRoom", "newsfeed", "coldOutreach", "emailServices", "performanceMarketing", "socialMedia", "seo", "saio", "embeddableWidgets", "referralSystem", "cro", "linkBuilding", "factChecker", "brandNaming", "podcastHosting", "surveys", "articles", "cla", "events",
    "salesOperations", "sellableProducts", "personalCards", "learningCenter",
    "aiModelTraining", "brandManagement", "digitalOperations", "sopsTemplates", "internalTraining", "annotations", "humanResources", "tasks", "businessMetrics", "meetings",
    "logsAudits", "admin", "superAdmin", "supportPortal",
    "scale2y3x", "growthHacking",
  ];
  const sectionToCategory: Record<keyof typeof openSections, SettingsCategory> = {
    cms: "Marketing", syntheticPersonas: "Marketing", focusGroups: "Marketing", collaborations: "Marketing", contentMarketing: "Marketing", newsRoom: "Marketing", newsfeed: "Marketing", coldOutreach: "Marketing", emailServices: "Marketing", performanceMarketing: "Marketing", socialMedia: "Marketing", seo: "Marketing", saio: "Marketing", embeddableWidgets: "Marketing", referralSystem: "Marketing", cro: "Marketing", linkBuilding: "Marketing", factChecker: "Marketing", brandNaming: "Marketing", podcastHosting: "Marketing", surveys: "Marketing", articles: "Marketing", cla: "Marketing", events: "Marketing",
    salesOperations: "Sales", sellableProducts: "Sales", personalCards: "Sales", learningCenter: "Sales",
    aiModelTraining: "Operations", brandManagement: "Operations", digitalOperations: "Operations", sopsTemplates: "Operations", internalTraining: "Operations", annotations: "Operations", humanResources: "Operations", tasks: "Operations", businessMetrics: "Operations", meetings: "Operations",
    logsAudits: "Admin", admin: "Admin", superAdmin: "Admin", supportPortal: "Admin",
    scale2y3x: "Systems", growthHacking: "Systems",
  };
  const sectionSearchIndex = useMemo(() => {
    const idx: Record<string, string[]> = {};
    for (const m of sectionMeta) {
      const items = sectionToItems[m.key];
      const inlineTitles = inlineSectionSearchTitles[m.key];
      if (inlineTitles !== undefined) {
        idx[m.key] = [m.title, ...inlineTitles];
      } else {
        idx[m.key] = items ? [m.title, ...items.map((i) => i.title)] : [m.title];
      }
    }
    return idx;
  }, [sectionMeta]);
  const returnTrue = useCallback((_key?: keyof typeof openSections) => true, []);
  const sectionVisible = useMemo(() => {
    if (!normalizedQuery) return returnTrue;
    return (key: keyof typeof openSections) => {
      const titles = sectionSearchIndex[key];
      return titles?.some((t) => t.toLowerCase().includes(normalizedQuery)) ?? false;
    };
  }, [normalizedQuery, sectionSearchIndex, returnTrue]);
  const firstVisibleSectionInCategory = useMemo(() => {
    const first: Partial<Record<SettingsCategory, keyof typeof openSections>> = {};
    for (const key of ORDERED_SECTIONS) {
      const cat = sectionToCategory[key];
      if (sectionVisible(key) && first[cat] === undefined) first[cat] = key;
    }
    return first;
  }, [normalizedQuery, sectionVisible]);
  const isFirstVisibleInCategory = (key: keyof typeof openSections) =>
    firstVisibleSectionInCategory[sectionToCategory[key]] === key;
  const sectionOrder: Record<keyof typeof openSections, number> = useMemo(() => {
    const o: Record<string, number> = {};
    ORDERED_SECTIONS.forEach((key, i) => { o[key] = i; });
    return o as Record<keyof typeof openSections, number>;
  }, []);
  const filterItemsBySearch = useCallback(<T extends { title: string }>(items: T[]): T[] => {
    if (!normalizedQuery) return items;
    return items.filter((item) => item.title.toLowerCase().includes(normalizedQuery));
  }, [normalizedQuery]);
  const itemMatchesSearch = useCallback(
    (itemTitle: string) => !normalizedQuery || itemTitle.toLowerCase().includes(normalizedQuery),
    [normalizedQuery]
  );
  const sectionHasVisibleItems = useCallback(
    (key: keyof typeof openSections) => {
      if (!normalizedQuery) return true;
      // If section title matches the search, show all items
      const sectionTitle = sectionMeta.find(m => m.key === key)?.title?.toLowerCase() || '';
      if (sectionTitle.includes(normalizedQuery)) return true;
      const items = sectionToItems[key];
      if (!items) return true;
      return filterItemsBySearch(items).length > 0;
    },
    [normalizedQuery, filterItemsBySearch, sectionMeta]
  );
  const CategorySeparator = ({ label }: { label: string }) => (
    <div className="flex items-center gap-2 px-2 py-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-[12px] shrink-0 flex-1 h-px bg-muted-foreground/50" />
    </div>
  );

  const handleMobileOpenChange = (open: boolean) => {
    setOpenMobile(open);
    if (!open) setIsSettingsSidebarOpen(false);
  };

  return (
    <Sidebar
      mobileSheetContentClassName="!bg-secondary/80 [&>div]:!bg-secondary/80"
      className="h-full w-full border-0 [&>div>div]:bg-secondary/80 [&_[data-active=true]]:!bg-background [&_[data-active=true]]:!text-primary [&_[data-active=true]]:font-semibold [&_[data-active=true]]:shadow-sm [&_[data-active=true]]:ring-1 [&_[data-active=true]]:ring-primary/20">
      <SidebarHeader className="!p-0 !pt-0">
        <div className="px-3 pb-3 pt-0 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Settings</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSettingsSidebarOpen(false)}
              className="h-8 w-8 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search settings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 bg-background"
            />
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="gap-0 flex flex-col">
        {sectionVisible("aiModelTraining") && sectionHasVisibleItems("aiModelTraining") && (
        <div style={{ order: sectionOrder.aiModelTraining }}>
        {isFirstVisibleInCategory("aiModelTraining") && <CategorySeparator label={sectionToCategory.aiModelTraining} />}
        <Collapsible open={openSections.aiModelTraining} onOpenChange={() => toggleSection('aiModelTraining')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.aiModelTraining ? 'bg-accent text-accent-foreground' : ''}`}>
                AI Model Training
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.aiModelTraining ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(aiModelTrainingSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("brandManagement") && sectionHasVisibleItems("brandManagement") && (
        <div style={{ order: sectionOrder.brandManagement }}>
        {isFirstVisibleInCategory("brandManagement") && <CategorySeparator label={sectionToCategory.brandManagement} />}
        <Collapsible open={openSections.brandManagement} onOpenChange={() => toggleSection('brandManagement')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.brandManagement ? 'bg-accent text-accent-foreground' : ''}`}>
                Brand Management
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.brandManagement ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(brandManagementSettingsItems).map((item) => {
                    // Check if this item requires a feature and if the feature is enabled
                    if ('featureKey' in item && !hasFeature(item.featureKey)) {
                      return null;
                    }
                    
                    return (
                      <SettingsMenuItemWithTutorial
                        key={item.title}
                        title={item.title}
                        path={item.path}
                        icon={item.icon}
                        isActive={location.pathname === item.path}
                        tutorialVideo={settingsVideos[item.path]}
                      />
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("logsAudits") && sectionHasVisibleItems("logsAudits") && (
        <div style={{ order: sectionOrder.logsAudits }}>
        {isFirstVisibleInCategory("logsAudits") && <CategorySeparator label={sectionToCategory.logsAudits} />}
        <Collapsible open={openSections.logsAudits} onOpenChange={() => toggleSection('logsAudits')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.logsAudits ? 'bg-accent text-accent-foreground' : ''}`}>
                Logs & Audits
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.logsAudits ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(logsAuditsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("contentMarketing") && sectionHasVisibleItems("contentMarketing") && (
        <div style={{ order: sectionOrder.contentMarketing }}>
        {isFirstVisibleInCategory("contentMarketing") && <CategorySeparator label={sectionToCategory.contentMarketing} />}
        <Collapsible open={openSections.contentMarketing} onOpenChange={() => toggleSection('contentMarketing')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.contentMarketing ? 'bg-accent text-accent-foreground' : ''}`}>
                Content Marketing
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.contentMarketing ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Prompts (Ideation)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/ideation-prompts'}
                     tooltip="Prompts (Ideation)"
                    >
                      <Link to="/ideation-prompts" className="w-full">
                        <Lightbulb />
                        <span>Prompts (Ideation)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Earned Media)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cm-prompts'}
                     tooltip="Prompts (Earned Media)"
                    >
                      <Link to="/cm-prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (Earned Media)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Podcasts)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/podcasts-prompts'}
                     tooltip="Prompts (Podcasts)"
                    >
                      <Link to="/podcasts-prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (Podcasts)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Press Releases)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/press-releases-prompts'}
                     tooltip="Prompts (Press Releases)"
                    >
                      <Link to="/press-releases-prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (Press Releases)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Quizzes)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/quiz-prompts'}
                     tooltip="Prompts (Quizzes)"
                    >
                      <Link to="/quiz-prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (Quizzes)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Feature Images)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/featureimage-prompts'}
                     tooltip="Prompts (Feature Images)"
                    >
                      <Link to="/featureimage-prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (Feature Images)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Mini Games)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/mini-game-prompts'}
                     tooltip="Prompts (Mini Games)"
                    >
                      <Link to="/mini-game-prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (Mini Games)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (Podcasts)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild 
                      isActive={location.pathname === '/podcasts-api-tokens'}
                      tooltip="API Tokens (Podcasts)"
                    >
                      <Link to="/podcasts-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens (Podcasts)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (HubSpot)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild 
                      isActive={location.pathname === '/hubspot-api-tokens'}
                      tooltip="API Tokens (HubSpot)"
                    >
                      <Link to="/hubspot-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens (HubSpot)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (GoHighLevel)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild 
                      isActive={location.pathname === '/ghl-api-tokens'}
                      tooltip="API Tokens (GoHighLevel)"
                    >
                      <Link to="/ghl-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens (GoHighLevel)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (Slack)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild 
                      isActive={location.pathname === '/slack-api-tokens'}
                      tooltip="API Tokens (Slack)"
                    >
                      <Link to="/slack-api-tokens" className="w-full">
                        <Send />
                        <span>API Tokens (Slack)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (Google Sheets)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild 
                      isActive={location.pathname === '/google-sheets-api-tokens'}
                      tooltip="API Tokens (Google Sheets)"
                    >
                      <Link to="/google-sheets-api-tokens" className="w-full">
                        <FileSpreadsheet />
                        <span>API Tokens (Google Sheets)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (CMS)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cms-api-tokens'}
                      tooltip="API Tokens (CMS)"
                    >
                      <Link to="/cms-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens (CMS)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (PPTs/PDFs)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/ppts-api-tokens'}
                      tooltip="API Tokens (PPTs/PDFs)"
                    >
                      <Link to="/ppts-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens (PPTs/PDFs)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (Earned Networks)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/earned-networks-api-tokens'}
                      tooltip="API Tokens (Earned Networks)"
                    >
                      <Link to="/earned-networks-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens (Earned Networks)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (SM Pillars)") && (
                   <SidebarMenuItem>
                     <SidebarMenuButton 
                       asChild 
                       isActive={location.pathname === '/content-pillar-prompts'}
                       tooltip="Prompts (SM Pillars)"
                      >
                        <Link to="/content-pillar-prompts" className="w-full">
                          <MessageSquare />
                          <span>Prompts (SM Pillars)</span>
                       </Link>
                     </SidebarMenuButton>
                   </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Key Dates)") && (
                   <SidebarMenuItem>
                     <SidebarMenuButton 
                       asChild 
                       isActive={location.pathname === '/keydates-prompts'}
                       tooltip="Prompts (Key Dates)"
                      >
                        <Link to="/keydates-prompts" className="w-full">
                          <Calendar />
                          <span>Prompts (Key Dates)</span>
                       </Link>
                     </SidebarMenuButton>
                   </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Visual Sizes & Dimensions") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/sm-sizes'}
                      tooltip="Visual Sizes & Dimensions"
                    >
                      <Link to="/sm-sizes" className="w-full">
                        <Smartphone />
                        <span>Visual Sizes & Dimensions</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens (Visual Models)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cm-api-tokens'}
                      tooltip="API Tokens (Visual Models)"
                    >
                      <Link to="/cm-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens (Visual Models)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Supported AI Creative Models") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/ai-creative-models'}
                      tooltip="Supported AI Creative Models"
                    >
                      <Link to="/ai-creative-models" className="w-full">
                        <Cpu />
                        <span>Supported AI Creative Models</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Settings (Short-Form)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/social-media-settings'}
                      tooltip="Settings (Short-Form)"
                    >
                      <Link to="/social-media-settings" className="w-full">
                        <Settings />
                        <span>Settings (Short-Form)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Settings (Long-Form)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cm-settings'}
                      tooltip="Settings (Long-Form)"
                    >
                      <Link to="/cm-settings" className="w-full">
                        <Settings />
                        <span>Settings (Long-Form)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts Embed Guide") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/prompt-store/embed-guide'}
                      tooltip="Prompts Embed Guide"
                    >
                      <Link to="/prompt-store/embed-guide" className="w-full">
                        <Globe />
                        <span>Prompts Embed Guide</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("newsRoom") && sectionHasVisibleItems("newsRoom") && (
        <div style={{ order: sectionOrder.newsRoom }}>
        {isFirstVisibleInCategory("newsRoom") && <CategorySeparator label={sectionToCategory.newsRoom} />}
        <Collapsible open={openSections.newsRoom} onOpenChange={() => toggleSection('newsRoom')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.newsRoom ? 'bg-accent text-accent-foreground' : ''}`}>
                News Room
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.newsRoom ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Prompts") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsroom/prompts'}
                      tooltip="Prompts (Newsroom)"
                    >
                      <Link to="/newsroom/prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Settings") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsroom/settings'}
                      tooltip="Settings (Newsroom)"
                    >
                      <Link to="/newsroom/settings" className="w-full">
                        <Settings />
                        <span>Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsroom/api-tokens'}
                      tooltip="API Tokens (HeyGen)"
                    >
                      <Link to="/newsroom/api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Email Settings") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsroom/press-releases/email-settings'}
                      tooltip="Email Settings (Press Releases)"
                    >
                      <Link to="/newsroom/press-releases/email-settings" className="w-full">
                        <Mail />
                        <span>Email Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("newsfeed") && sectionHasVisibleItems("newsfeed") && (
        <div style={{ order: sectionOrder.newsfeed }}>
        {isFirstVisibleInCategory("newsfeed") && <CategorySeparator label={sectionToCategory.newsfeed} />}
        <Collapsible open={openSections.newsfeed} onOpenChange={() => toggleSection('newsfeed')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.newsfeed ? 'bg-accent text-accent-foreground' : ''}`}>
                Newsfeed
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.newsfeed ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("AI Prompts") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsfeed/prompts'}
                      tooltip="AI Prompts (Newsfeed)"
                    >
                      <Link to="/newsfeed/prompts" className="w-full">
                        <MessageSquare />
                        <span>AI Prompts</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Newsfeed Embed Guide") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsfeed/embed-guide'}
                      tooltip="Newsfeed Embed Guide"
                    >
                      <Link to="/newsfeed/embed-guide" className="w-full">
                        <Globe />
                        <span>Newsfeed Embed Guide</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Leads Webhook Guide") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsfeed/leads-webhook-guide'}
                      tooltip="Leads Webhook Guide"
                    >
                      <Link to="/newsfeed/leads-webhook-guide" className="w-full">
                        <Webhook />
                        <span>Leads Webhook Guide</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Email Settings") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/newsfeed/email-settings'}
                      tooltip="Email Settings"
                    >
                      <Link to="/newsfeed/email-settings" className="w-full">
                        <Mail />
                        <span>Email Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("coldOutreach") && sectionHasVisibleItems("coldOutreach") && (
        <div style={{ order: sectionOrder.coldOutreach }}>
        {isFirstVisibleInCategory("coldOutreach") && <CategorySeparator label={sectionToCategory.coldOutreach} />}
        <Collapsible open={openSections.coldOutreach} onOpenChange={() => toggleSection('coldOutreach')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.coldOutreach ? 'bg-accent text-accent-foreground' : ''}`}>
                Cold Outreach
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.coldOutreach ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Prompts") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cold-outreach/prompts'}
                      tooltip="Prompts (Cold Outreach)"
                    >
                      <Link to="/cold-outreach/prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Email Settings") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cold-outreach/email-settings'}
                      tooltip="Email Settings (Cold Outreach)"
                    >
                      <Link to="/cold-outreach/email-settings" className="w-full">
                        <Mail />
                        <span>Email Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("emailServices") && sectionHasVisibleItems("emailServices") && (
        <div style={{ order: sectionOrder.emailServices }}>
        {isFirstVisibleInCategory("emailServices") && <CategorySeparator label={sectionToCategory.emailServices} />}
        <Collapsible open={openSections.emailServices} onOpenChange={() => toggleSection('emailServices')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.emailServices ? 'bg-accent text-accent-foreground' : ''}`}>
                Email Services
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.emailServices ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Email Providers") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/email-providers'}
                      tooltip="Email Providers"
                    >
                      <Link to="/email-providers" className="w-full">
                        <Mail />
                        <span>Email Providers</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("collaborations") && sectionHasVisibleItems("collaborations") && (
        <div style={{ order: sectionOrder.collaborations }}>
        {isFirstVisibleInCategory("collaborations") && <CategorySeparator label={sectionToCategory.collaborations} />}
        <Collapsible open={openSections.collaborations} onOpenChange={() => toggleSection('collaborations')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.collaborations ? 'bg-accent text-accent-foreground' : ''}`}>
                Collaborations
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.collaborations ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Prompts") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/collaborations/prompts' || location.pathname === '/collaborations/prompts/new' || location.pathname.startsWith('/collaborations/prompts/') && location.pathname.includes('/edit')}
                      tooltip="Prompts (Collaborations)"
                    >
                      <Link to="/collaborations/prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Settings") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/collaborations/settings'}
                      tooltip="Settings (Collaborations)"
                    >
                      <Link to="/collaborations/settings" className="w-full">
                        <Settings />
                        <span>Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("salesOperations") && sectionHasVisibleItems("salesOperations") && (
        <div style={{ order: sectionOrder.salesOperations }}>
        {isFirstVisibleInCategory("salesOperations") && <CategorySeparator label={sectionToCategory.salesOperations} />}
        <Collapsible open={openSections.salesOperations} onOpenChange={() => toggleSection('salesOperations')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.salesOperations ? 'bg-accent text-accent-foreground' : ''}`}>
                Sales
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.salesOperations ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Sales Settings") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/sales-settings'}
                      tooltip="Sales Settings"
                    >
                      <Link to="/sales-settings" className="w-full">
                        <Settings />
                        <span>Sales Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts (Sales)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/sales-operations/prompts'}
                      tooltip="Prompts (Sales)"
                    >
                      <Link to="/sales-operations/prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (Sales)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("AI Training Feedback") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/sales-operations/ai-training-feedback'}
                      tooltip="AI Training Feedback"
                    >
                      <Link to="/sales-operations/ai-training-feedback" className="w-full">
                        <MessageSquare />
                        <span>AI Training Feedback</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("performanceMarketing") && sectionHasVisibleItems("performanceMarketing") && (
        <div style={{ order: sectionOrder.performanceMarketing }}>
        {isFirstVisibleInCategory("performanceMarketing") && <CategorySeparator label={sectionToCategory.performanceMarketing} />}
        <Collapsible open={openSections.performanceMarketing} onOpenChange={() => toggleSection('performanceMarketing')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.performanceMarketing ? 'bg-accent text-accent-foreground' : ''}`}>
                Paid Ads
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.performanceMarketing ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("PM Prompts") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/pm-prompts'}
                      tooltip="PM Prompts"
                    >
                      <Link to="/pm-prompts" className="w-full">
                        <FileText />
                        <span>PM Prompts</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Paid Ads Integrations") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/performance-marketing-settings'}
                      tooltip="Paid Ads Integrations"
                    >
                      <Link to="/performance-marketing-settings" className="w-full">
                        <Settings />
                        <span>Paid Ads Integrations</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Brief Prompts") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/performance-brief/prompts'}
                      tooltip="Brief Prompts"
                    >
                      <Link to="/performance-brief/prompts" className="w-full">
                        <FileText />
                        <span>Brief Prompts</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("seo") && sectionHasVisibleItems("seo") && (
        <div style={{ order: sectionOrder.seo }}>
        {isFirstVisibleInCategory("seo") && <CategorySeparator label={sectionToCategory.seo} />}
        <Collapsible open={openSections.seo} onOpenChange={() => toggleSection('seo')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.seo ? 'bg-accent text-accent-foreground' : ''}`}>
                SEO
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.seo ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("API Tokens") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/seo-api-tokens'}
                      tooltip="API Tokens"
                    >
                      <Link to="/seo-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {filterItemsBySearch(seoSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("socialMedia") && sectionHasVisibleItems("socialMedia") && (
        <div style={{ order: sectionOrder.socialMedia }}>
        {isFirstVisibleInCategory("socialMedia") && <CategorySeparator label={sectionToCategory.socialMedia} />}
        <Collapsible open={openSections.socialMedia} onOpenChange={() => toggleSection('socialMedia')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.socialMedia ? 'bg-accent text-accent-foreground' : ''}`}>
                Social Media Management
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.socialMedia ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Instagram Data Collector") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link 
                        to="/instagram-data-collector"
                        className={location.pathname === '/instagram-data-collector' ? 'bg-muted text-primary font-medium' : 'hover:bg-muted/50'}
                      >
                        <Instagram className="mr-2 h-4 w-4" />
                        Instagram Data Collector
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("saio") && sectionHasVisibleItems("saio") && (
        <div style={{ order: sectionOrder.saio }}>
        {isFirstVisibleInCategory("saio") && <CategorySeparator label={sectionToCategory.saio} />}
        <Collapsible open={openSections.saio} onOpenChange={() => toggleSection('saio')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.saio ? 'bg-accent text-accent-foreground' : ''}`}>
                GEO (aka SAIO)
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.saio ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("API Tokens") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/saio-api-tokens'}
                      tooltip="API Tokens"
                    >
                      <Link to="/saio-api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("Prompts") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/saio-settings'}
                      tooltip="Prompts"
                    >
                      <Link to="/saio-settings" className="w-full">
                        <Settings />
                        <span>Prompts</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("syntheticPersonas") && sectionHasVisibleItems("syntheticPersonas") && (
        <div style={{ order: sectionOrder.syntheticPersonas }}>
        {isFirstVisibleInCategory("syntheticPersonas") && <CategorySeparator label={sectionToCategory.syntheticPersonas} />}
        <Collapsible open={openSections.syntheticPersonas} onOpenChange={() => toggleSection('syntheticPersonas')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.syntheticPersonas ? 'bg-accent text-accent-foreground' : ''}`}>
                Synthetic Personas
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.syntheticPersonas ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <div className="px-4 py-2 text-sm text-muted-foreground">
                  No settings items yet.
                </div>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("focusGroups") && sectionHasVisibleItems("focusGroups") && (
        <div style={{ order: sectionOrder.focusGroups }}>
        {isFirstVisibleInCategory("focusGroups") && <CategorySeparator label={sectionToCategory.focusGroups} />}
        <Collapsible open={openSections.focusGroups} onOpenChange={() => toggleSection('focusGroups')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.focusGroups ? 'bg-accent text-accent-foreground' : ''}`}>
                Focus Groups
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.focusGroups ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <div className="px-4 py-2 text-sm text-muted-foreground">
                  No settings items yet.
                </div>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("digitalOperations") && sectionHasVisibleItems("digitalOperations") && (
        <div style={{ order: sectionOrder.digitalOperations }}>
        {isFirstVisibleInCategory("digitalOperations") && <CategorySeparator label={sectionToCategory.digitalOperations} />}
        <Collapsible open={openSections.digitalOperations} onOpenChange={() => toggleSection('digitalOperations')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.digitalOperations ? 'bg-accent text-accent-foreground' : ''}`}>
                Tasks
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.digitalOperations ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(digitalOperationsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("sopsTemplates") && sectionHasVisibleItems("sopsTemplates") && (
        <div style={{ order: sectionOrder.sopsTemplates }}>
        {isFirstVisibleInCategory("sopsTemplates") && <CategorySeparator label={sectionToCategory.sopsTemplates} />}
        <Collapsible open={openSections.sopsTemplates} onOpenChange={() => toggleSection('sopsTemplates')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.sopsTemplates ? 'bg-accent text-accent-foreground' : ''}`}>
                SOPs
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.sopsTemplates ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(sopsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("growthHacking") && sectionHasVisibleItems("growthHacking") && (
        <div style={{ order: sectionOrder.growthHacking }}>
        {isFirstVisibleInCategory("growthHacking") && <CategorySeparator label={sectionToCategory.growthHacking} />}
        <Collapsible open={openSections.growthHacking} onOpenChange={() => toggleSection('growthHacking')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.growthHacking ? 'bg-accent text-accent-foreground' : ''}`}>
                Growth Hacking
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.growthHacking ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(growthHackingSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("embeddableWidgets") && sectionHasVisibleItems("embeddableWidgets") && (
        <div style={{ order: sectionOrder.embeddableWidgets }}>
        {isFirstVisibleInCategory("embeddableWidgets") && <CategorySeparator label={sectionToCategory.embeddableWidgets} />}
        <Collapsible open={openSections.embeddableWidgets} onOpenChange={() => toggleSection('embeddableWidgets')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.embeddableWidgets ? 'bg-accent text-accent-foreground' : ''}`}>
                Embeddable Widgets
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.embeddableWidgets ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(embeddableWidgetsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("referralSystem") && sectionHasVisibleItems("referralSystem") && (
        <div style={{ order: sectionOrder.referralSystem }}>
        {isFirstVisibleInCategory("referralSystem") && <CategorySeparator label={sectionToCategory.referralSystem} />}
        <Collapsible open={openSections.referralSystem} onOpenChange={() => toggleSection('referralSystem')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.referralSystem ? 'bg-accent text-accent-foreground' : ''}`}>
                {tNav('referralSystem')}
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.referralSystem ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(referralSystemSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("cro") && sectionHasVisibleItems("cro") && (
        <div style={{ order: sectionOrder.cro }}>
        {isFirstVisibleInCategory("cro") && <CategorySeparator label={sectionToCategory.cro} />}
        <Collapsible open={openSections.cro} onOpenChange={() => toggleSection('cro')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.cro ? 'bg-accent text-accent-foreground' : ''}`}>
                CRO / UX
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.cro ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {itemMatchesSearch("Prompts (CRO)") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cro/prompts'}
                      tooltip="Prompts (CRO)"
                    >
                      <Link to="/cro/prompts" className="w-full">
                        <MessageSquare />
                        <span>Prompts (CRO)</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                  {itemMatchesSearch("API Tokens") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === '/cro/api-tokens'}
                      tooltip="API Tokens"
                    >
                      <Link to="/cro/api-tokens" className="w-full">
                        <Key />
                        <span>API Tokens</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("linkBuilding") && sectionHasVisibleItems("linkBuilding") && (
        <div style={{ order: sectionOrder.linkBuilding }}>
        {isFirstVisibleInCategory("linkBuilding") && <CategorySeparator label={sectionToCategory.linkBuilding} />}
        <Collapsible open={openSections.linkBuilding} onOpenChange={() => toggleSection('linkBuilding')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.linkBuilding ? 'bg-accent text-accent-foreground' : ''}`}>
                Link Building
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.linkBuilding ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(linkBuildingSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("scale2y3x") && sectionHasVisibleItems("scale2y3x") && (
        <div style={{ order: sectionOrder.scale2y3x }}>
        {isFirstVisibleInCategory("scale2y3x") && <CategorySeparator label={sectionToCategory.scale2y3x} />}
        <Collapsible open={openSections.scale2y3x} onOpenChange={() => toggleSection('scale2y3x')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.scale2y3x ? 'bg-accent text-accent-foreground' : ''}`}>
                Scale at Speed: 2Y3X
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.scale2y3x ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(scale2y3xSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("factChecker") && sectionHasVisibleItems("factChecker") && (
        <div style={{ order: sectionOrder.factChecker }}>
        {isFirstVisibleInCategory("factChecker") && <CategorySeparator label={sectionToCategory.factChecker} />}
        <Collapsible open={openSections.factChecker} onOpenChange={() => toggleSection('factChecker')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.factChecker ? 'bg-accent text-accent-foreground' : ''}`}>
                Fact Checker
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.factChecker ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(factCheckerSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("brandNaming") && sectionHasVisibleItems("brandNaming") && (
        <div style={{ order: sectionOrder.brandNaming }}>
        {isFirstVisibleInCategory("brandNaming") && <CategorySeparator label={sectionToCategory.brandNaming} />}
        <Collapsible open={openSections.brandNaming} onOpenChange={() => toggleSection('brandNaming')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.brandNaming ? 'bg-accent text-accent-foreground' : ''}`}>
                Brand Naming
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.brandNaming ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(brandNamingSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("sellableProducts") && sectionHasVisibleItems("sellableProducts") && (
        <div style={{ order: sectionOrder.sellableProducts }}>
        {isFirstVisibleInCategory("sellableProducts") && <CategorySeparator label={sectionToCategory.sellableProducts} />}
        <Collapsible open={openSections.sellableProducts} onOpenChange={() => toggleSection('sellableProducts')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.sellableProducts ? 'bg-accent text-accent-foreground' : ''}`}>
                Sellable Products
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.sellableProducts ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(sellableProductsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("personalCards") && sectionHasVisibleItems("personalCards") && (
        <div style={{ order: sectionOrder.personalCards }}>
        {isFirstVisibleInCategory("personalCards") && <CategorySeparator label={sectionToCategory.personalCards} />}
        <Collapsible open={openSections.personalCards} onOpenChange={() => toggleSection('personalCards')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.personalCards ? 'bg-accent text-accent-foreground' : ''}`}>
                Personal Cards
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.personalCards ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(personalCardsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("learningCenter") && sectionHasVisibleItems("learningCenter") && (
        <div style={{ order: sectionOrder.learningCenter }}>
        {isFirstVisibleInCategory("learningCenter") && <CategorySeparator label={sectionToCategory.learningCenter} />}
        <Collapsible open={openSections.learningCenter} onOpenChange={() => toggleSection('learningCenter')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.learningCenter ? 'bg-accent text-accent-foreground' : ''}`}>
                Learning Center
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.learningCenter ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(learningCenterSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("events") && sectionHasVisibleItems("events") && (
        <div style={{ order: sectionOrder.events }}>
        {isFirstVisibleInCategory("events") && <CategorySeparator label={sectionToCategory.events} />}
        <Collapsible open={openSections.events} onOpenChange={() => toggleSection('events')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.events ? 'bg-accent text-accent-foreground' : ''}`}>
                Events
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.events ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(eventsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("internalTraining") && sectionHasVisibleItems("internalTraining") && (
        <div style={{ order: sectionOrder.internalTraining }}>
        {isFirstVisibleInCategory("internalTraining") && <CategorySeparator label={sectionToCategory.internalTraining} />}
        <Collapsible open={openSections.internalTraining} onOpenChange={() => toggleSection('internalTraining')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.internalTraining ? 'bg-accent text-accent-foreground' : ''}`}>
                Internal Training
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.internalTraining ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(internalTrainingSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("surveys") && sectionHasVisibleItems("surveys") && (
        <div style={{ order: sectionOrder.surveys }}>
        {isFirstVisibleInCategory("surveys") && <CategorySeparator label={sectionToCategory.surveys} />}
        <Collapsible open={openSections.surveys} onOpenChange={() => toggleSection('surveys')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.surveys ? 'bg-accent text-accent-foreground' : ''}`}>
                Surveys
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.surveys ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(surveysSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("annotations") && sectionHasVisibleItems("annotations") && (
        <div style={{ order: sectionOrder.annotations }}>
        {isFirstVisibleInCategory("annotations") && <CategorySeparator label={sectionToCategory.annotations} />}
        <Collapsible open={openSections.annotations} onOpenChange={() => toggleSection('annotations')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.annotations ? 'bg-accent text-accent-foreground' : ''}`}>
                Annotations
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.annotations ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(annotationsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("cla") && sectionHasVisibleItems("cla") && (
        <div style={{ order: sectionOrder.cla }}>
        {isFirstVisibleInCategory("cla") && <CategorySeparator label={sectionToCategory.cla} />}
        <Collapsible open={openSections.cla} onOpenChange={() => toggleSection('cla')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.cla ? 'bg-accent text-accent-foreground' : ''}`}>
                Competitive Analysis
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.cla ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(claSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("humanResources") && sectionHasVisibleItems("humanResources") && (
        <div style={{ order: sectionOrder.humanResources }}>
        {isFirstVisibleInCategory("humanResources") && <CategorySeparator label={sectionToCategory.humanResources} />}
        <Collapsible open={openSections.humanResources} onOpenChange={() => toggleSection('humanResources')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.humanResources ? 'bg-accent text-accent-foreground' : ''}`}>
                {tNav('humanResources') || "Human Resources"}
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.humanResources ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(humanResourcesSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("tasks") && sectionHasVisibleItems("tasks") && (
        <div style={{ order: sectionOrder.tasks }}>
        {isFirstVisibleInCategory("tasks") && <CategorySeparator label={sectionToCategory.tasks} />}
        <Collapsible open={openSections.tasks} onOpenChange={() => toggleSection('tasks')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.tasks ? 'bg-accent text-accent-foreground' : ''}`}>
                Reports
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.tasks ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(tasksSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("businessMetrics") && sectionHasVisibleItems("businessMetrics") && (
        <div style={{ order: sectionOrder.businessMetrics }}>
        {isFirstVisibleInCategory("businessMetrics") && <CategorySeparator label={sectionToCategory.businessMetrics} />}
        <Collapsible open={openSections.businessMetrics} onOpenChange={() => toggleSection('businessMetrics')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.businessMetrics ? 'bg-accent text-accent-foreground' : ''}`}>
                Business Metrics
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.businessMetrics ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(businessMetricsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("cms") && sectionHasVisibleItems("cms") && (
        <div style={{ order: sectionOrder.cms }}>
        {isFirstVisibleInCategory("cms") && <CategorySeparator label={sectionToCategory.cms} />}
        <Collapsible open={openSections.cms} onOpenChange={() => toggleSection('cms')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.cms ? 'bg-accent text-accent-foreground' : ''}`}>
                CMS
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.cms ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(cmsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("admin") && sectionHasVisibleItems("admin") && (
        <div style={{ order: sectionOrder.admin }}>
        {isFirstVisibleInCategory("admin") && <CategorySeparator label={sectionToCategory.admin} />}
        <Collapsible open={openSections.admin} onOpenChange={() => toggleSection('admin')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.admin ? 'bg-accent text-accent-foreground' : ''}`}>
                Admin
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.admin ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(adminSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("supportPortal") && sectionHasVisibleItems("supportPortal") && (
        <div style={{ order: sectionOrder.supportPortal }}>
        {isFirstVisibleInCategory("supportPortal") && <CategorySeparator label={sectionToCategory.supportPortal} />}
        <Collapsible open={openSections.supportPortal} onOpenChange={() => toggleSection('supportPortal')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.supportPortal ? 'bg-accent text-accent-foreground' : ''}`}>
                Support Portal
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.supportPortal ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(supportPortalSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("podcastHosting") && sectionHasVisibleItems("podcastHosting") && (
        <div style={{ order: sectionOrder.podcastHosting }}>
        {isFirstVisibleInCategory("podcastHosting") && <CategorySeparator label={sectionToCategory.podcastHosting} />}
        <Collapsible open={openSections.podcastHosting} onOpenChange={() => toggleSection('podcastHosting')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.podcastHosting ? 'bg-accent text-accent-foreground' : ''}`}>
                Podcast Hosting
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.podcastHosting ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(podcastHostingSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("meetings") && sectionHasVisibleItems("meetings") && (
        <div style={{ order: sectionOrder.meetings }}>
        {isFirstVisibleInCategory("meetings") && <CategorySeparator label={sectionToCategory.meetings} />}
        <Collapsible open={openSections.meetings} onOpenChange={() => toggleSection('meetings')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className={`cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium px-2 py-1.5 transition-colors ${openSections.meetings ? 'bg-accent text-accent-foreground' : ''}`}>
                Meetings
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.meetings ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filterItemsBySearch(meetingsSettingsItems).map((item) => (
                    <SettingsMenuItemWithTutorial
                      key={item.title}
                      title={item.title}
                      path={item.path}
                      icon={item.icon}
                      isActive={location.pathname === item.path}
                      tutorialVideo={settingsVideos[item.path]}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}

        {sectionVisible("superAdmin") && sectionHasVisibleItems("superAdmin") && (
        <div style={{ order: sectionOrder.superAdmin }}>
        {isFirstVisibleInCategory("superAdmin") && <CategorySeparator label={sectionToCategory.superAdmin} />}
        <Collapsible open={openSections.superAdmin} onOpenChange={() => toggleSection('superAdmin')}>
          <SidebarGroup className="py-2">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md flex items-center justify-between text-sm font-medium text-red-600 dark:text-red-400">
                Super Admin
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.superAdmin ? 'rotate-180' : ''}`} />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SuperAdminNavigation isSuperAdmin={isSuperAdmin} isLoading={superAdminLoading} />
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
        </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}